import { type FC, lazy, Suspense } from "react"

import type { LandingPageFeaturesBlock } from "@forento/core/models/landing"
import { isLandingFeatureIcon } from "@forento/core/models/landingFeatureIcon"
import LandingFeatureIcon from "@forento/ui/components/LandingFeatureIcon"
import { cn } from "@forento/ui/utilities/tailwind"

const RichTextPreview = lazy(() => import("@forento/ui/components/RichTextPreview"))

type Props = { block: LandingPageFeaturesBlock }
const FeaturesBlockPreview: FC<Props> = ({ block }) => {
	if (block.content.features.length === 0) return null

	return (
		<section className="px-6 pt-16 pb-0 sm:pt-20 lg:px-8">
			<div className="mx-auto max-w-7xl">
				{block.content.title && (
					<h2 className="text-landing-foreground mb-12 text-center text-3xl font-bold tracking-tight sm:text-4xl">
						{block.content.title}
					</h2>
				)}
				<div
					className={cn(
						"grid gap-8",
						block.content.features.length === 1 && "mx-auto max-w-2xl grid-cols-1",
						block.content.features.length === 2 && "sm:grid-cols-2",
						block.content.features.length >= 3 && "sm:grid-cols-2 lg:grid-cols-3",
						block.content.features.length >= 4 && "lg:grid-cols-4",
					)}
				>
					{block.content.features.map((feature, index) => (
						<div
							key={index}
							className="bg-landing-surface ring-landing-border/50 relative flex flex-col rounded-2xl p-8 shadow-lg ring-1 shadow-gray-900/5"
						>
							{feature.icon && (
								<div className="landing-primary-text from-landing-surface-muted to-landing-border/60 ring-landing-border/50 mb-6 flex size-14 items-center justify-center rounded-xl bg-linear-to-br ring-1">
									{isLandingFeatureIcon(feature.icon) ? (
										<LandingFeatureIcon value={feature.icon} className="size-7" />
									) : (
										<img src={feature.icon} alt={feature.title} className="size-7 object-contain" />
									)}
								</div>
							)}
							<h3 className="text-landing-foreground text-xl font-bold tracking-tight">
								{feature.title}
							</h3>
							{feature.description && (
								<Suspense>
									<RichTextPreview
										value={feature.description}
										className="text-landing-foreground-muted mt-3 text-base leading-7 [&>p]:mb-3 [&>p:last-child]:mb-0"
									/>
								</Suspense>
							)}
						</div>
					))}
				</div>
			</div>
		</section>
	)
}

export default FeaturesBlockPreview
